import { useDispatch, useSelector } from 'react-redux';
import { useRef } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import clsx from 'clsx';
import { selectSelectedLanguage } from '../../pages/store/language/selectors';
import { CountryLanguage } from '../../constants/leng';
import { changeLanguage } from '../../pages/store/language';
import { Language } from '../../configs/language';
import { selectProfileData } from '../../pages/profile/store/selectors';
import useBoolean from '../../hooks/useBoolean';
import useClickOutside from '../../hooks/useClickOutside';
import langSelectorStyles from './lang.styles';
import { NAVBAR_LANGUAGES_OPTIONS } from './constants/navbar.constants';

const LangSelector = () => {
  const selectLanguage = useSelector(selectSelectedLanguage);
  const dispatch = useDispatch();
  const userProfile = useSelector(selectProfileData);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, { toggle: toggleDropdown, setFalse: closeDropdown }] =
    useBoolean(false);

  useClickOutside(containerRef, closeDropdown);

  const currentLanguage =
    NAVBAR_LANGUAGES_OPTIONS.find(
      (lang) => lang.id === selectLanguage.language
    ) || NAVBAR_LANGUAGES_OPTIONS[0];

  const handleChange = (value: string) => {
    dispatch(
      changeLanguage({
        country: userProfile?.company?.country as CountryLanguage,
        language: value as Language,
      })
    );
    closeDropdown();
  };

  const handleToggle = () => {
    toggleDropdown();
  };

  return (
    <Box
      ref={containerRef}
      className={langSelectorStyles.langSelectorContainer}
    >
      <Box
        onClick={handleToggle}
        className={clsx(
          langSelectorStyles.langSelectorButton,
          isMobile && langSelectorStyles.langSelectorButtonMobile
        )}
        component="button"
        type="button"
      >
        <Box className={langSelectorStyles.langSelectorContent}>
          {currentLanguage.icon}
          <span
            className={clsx(
              langSelectorStyles.langSelectorText,
              isMobile
                ? langSelectorStyles.langSelectorTextMobile
                : langSelectorStyles.langSelectorTextDesktop
            )}
          >
            {isMobile ? currentLanguage.abbreviation : currentLanguage.value}
          </span>
        </Box>
        <KeyboardArrowDownIcon />
      </Box>

      {isOpen && (
        <Box className={langSelectorStyles.dropdown}>
          {NAVBAR_LANGUAGES_OPTIONS.map((option) => (
            <Box
              key={option.id}
              onClick={() => handleChange(option.id)}
              className={langSelectorStyles.dropdownOption}
              component="button"
              type="button"
            >
              {option.icon}
              <span>{isMobile ? option.abbreviation : option.value}</span>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default LangSelector;
